/**
 * RETURNED PRACTICE DOCUMENTS AS THE TIMELINE'S FOURTH SOURCE —
 * `tasks/practice-documents.md` §6 item 3, Phase 6.
 *
 * The clinical timeline aggregates notes, admission records and WhatsApp. §6 item 3 makes returned
 * documents a fourth alongside them. This file is the whole of the mapping, and it is a PURE
 * module with no Angular in it — the same shape as `clinical-timeline-search-core.ts` beside it —
 * so that "a NULL score does not render as 0" is a claim testable in isolation rather than a claim
 * about a 1,220-line component with nineteen injected collaborators.
 *
 * ── WHAT APPEARS, AND WHAT DELIBERATELY DOES NOT ─────────────────────────────────────────────
 *
 * ONLY a document a CLINICIAN HAS VALIDATED: `status === 'completed'`, a `returnedAt`, AND
 * `reviewStatus === 'validated'`. All three.
 *
 * §26 made the third one the point of this filter. A returned document now passes through two
 * people — somebody signs it off as the right document, and a clinician then reads it, records
 * what they observed and puts it on the record — and the timeline draws the SECOND step, not the
 * first. Nothing reaches a patient's clinical record that a clinician has not put their name to.
 *
 * ── THE THING THAT WILL LOOK LIKE A BUG, AND IS NOT ──────────────────────────────────────────
 *
 * **Every document returned before §26 shipped disappears from the timeline until somebody
 * validates it.** There is no backfill migration and there deliberately is not one: marking the
 * existing `accepted` rows as validated would assert that a clinician read documents no clinician
 * ever opened, on a clinical record. They are all still in the documents panel, which is where
 * they are worked through.
 *
 * The older exclusions still hold and still hold for their own reasons:
 *
 *   · an OUTSTANDING ask belongs to the panel, not to a clinical record — the timeline answers
 *     "what is in this patient's file", and a document nobody has sent back is not in it;
 *   · a REJECTED return leaves the moment it is rejected, because §13.2 re-opens the same row
 *     (`status` back to `'sent'`, `returnedAt` cleared, the files deactivated). The practice
 *     decided those pages were not the document, and a timeline that kept showing them would be
 *     showing a clinician something the practice has already refused;
 *   · an INFORMATION-ONLY send (`status: 'delivered'`, §14.4) never appears. Nothing came back;
 *     there is nothing to put in the record;
 *   · and now a SIGNED-OFF but not yet validated return, which is a document waiting for a
 *     clinician rather than one a clinician has accepted.
 *
 * ── `scoreTotal` NULL AND `scoreMax` NULL MUST NOT RENDER AS 0 ───────────────────────────────
 *
 * The principle is §3.2 finding 4's, applied to the score: "could not tell" and "scored zero" are
 * different facts. Most documents do not score at all, so NULL/NULL is the ORDINARY case here —
 * and a timeline row reading "0" against a patient's returned questionnaire is a clinical signal
 * that was never measured. `documentScoreLabel` returns `null` for that case and the template
 * renders no score element at all, while a genuine zero renders as "0 / 10".
 *
 * The two columns are decided INDEPENDENTLY on the server (`utils/patientDocumentAiRead.js`), so a
 * document whose pages give item scores but never state a denominator arrives as total-with-no-max
 * and is rendered as the bare number. Nothing here invents the missing half.
 */

import { FileObject, mapMediaToFileObject } from '../../../../core/interfaces/file-object';
import {
  PatientDocumentRequestDto, PatientDocumentReturnedFileDto,
  activeReturnedFiles, activeValidationFiles,
} from '../../../../core/services/patient-document-request.service';

/** The `type` discriminator the timeline template and the search indexer switch on. */
export const TIMELINE_DOCUMENT_TYPE = 'document-return';

export interface TimelineDocumentItem {
  type: typeof TIMELINE_DOCUMENT_TYPE;
  /** The request id. The panel is opened on it, so it has to survive the mapping. */
  id: string;
  /**
   * Whose row this is. Every other timeline row has one, and `sendTimelineRecord` posts it as the
   * `patientId` of the communication — without it the email 500s on the server. See the mapping.
   */
  patientId: string | null;
  /** The day the timeline groups on — `returnedAt`, never `createdAt`. See `toTimelineDocuments`. */
  date: string;
  /** What the component orders WITHIN a day group by. Same instant as `date`. */
  createdAt: string;
  documentName: string;
  reviewStatus: string | null;
  /** Practice-user wording for the review state. Never a raw enum value. */
  reviewLabel: string;
  returnedFileCount: number;
  scoreTotal: number | null;
  scoreMax: number | null;
  /** `null` when the document does not score. NEVER the string '0'. See the header. */
  scoreLabel: string | null;
  /** Whether an AI read produced anything. Distinct from "it was returned". */
  hasAiSummary: boolean;

  /* ── §26. Who put this on the record, and what they saw ──────────────────────────────── */

  /** The clinician's name, or null on a row from before §26. Never a raw id. */
  validatedByName: string | null;
  validatedAt: string | null;
  /**
   * Their observations. Filled in by the DETAIL read, not by the list: the note is encrypted and
   * `toStaffDto` strips it from every list response, exactly as it strips `reviewNote`.
   */
  validationNote: string | null;

  /**
   * THE RETURNED PAGES, ready for `<app-file-gallery>` — empty until the detail read lands.
   *
   * Empty is the honest starting state and not a bug: the LIST endpoint serves
   * `returnedFileCount` and no files at all (see the DTO), so the pages arrive from a per-row
   * `GET /patient-document-requests/:id` that the component fires once the row exists. A row that
   * has not been read yet, or whose read failed, draws no gallery rather than an empty frame.
   */
  galleryFiles: FileObject[];

  /**
   * When the URLs in `galleryFiles` die, as epoch ms — 0 when there are none.
   *
   * A returned page's link is a 15-MINUTE SAS (`DEFAULT_SAS_MINUTES`), minted per read and
   * deliberately short: unlike note media, which is stored with a ten-year token and can therefore
   * sit in a list forever, these are the patient's own returned documents. A gallery nobody has to
   * click open will outlive its links, so the component re-reads the row as this approaches rather
   * than leaving broken pages on a clinical record. See `documentGalleryIsStale`.
   */
  galleryExpiresAt: number;
}

/**
 * The review state in words a receptionist uses.
 *
 * `awaiting_review` is the state every return starts in (§0 decision 3) and it is the one that
 * needs the strongest wording on a clinical timeline — a returned document nobody has looked at is
 * the exact failure the review loop exists to prevent.
 */
const REVIEW_LABELS: { [key: string]: string } = {
  awaiting_review: 'Needs review',
  // §26 — a waypoint, not an end state. Neither of the first three can reach a timeline row under
  // the filter above; they are kept because `documentReviewLabel` is also the panel's vocabulary.
  accepted: 'Signed off — needs validation',
  rejected: 'Rejected',
  validated: 'Validated'
};

export function documentReviewLabel(reviewStatus: string | null | undefined): string {
  if (!reviewStatus) {
    // A completed return with no review state at all predates decision 3's column being written,
    // or is a row some other path completed. "Returned" is the honest thing to say about it; the
    // one thing that must not be said is "Accepted".
    return 'Returned';
  }
  return REVIEW_LABELS[reviewStatus] || 'Returned';
}

/**
 * The score, as a reviewer should read it — or `null`, which means DO NOT DRAW A SCORE.
 *
 * Four cases, and the first is the common one:
 *
 *   | scoreTotal | scoreMax | renders as        | means                                    |
 *   |------------|----------|-------------------|------------------------------------------|
 *   | null       | null     | null (nothing)    | the document does not score, or no read  |
 *   | 0          | 10       | "0 / 10"          | the patient genuinely scored zero        |
 *   | 14         | null     | "14"              | item scores, no stated denominator       |
 *   | null       | 10       | "— / 10"          | a maximum was read and a total was not   |
 *
 * The em dash in the last row is deliberately not a zero and deliberately not an omission: the
 * document says it is out of ten and the read could not tell what was scored, and both halves of
 * that are things a reviewer needs before opening the file.
 */
export function documentScoreLabel(
  scoreTotal: number | null | undefined,
  scoreMax: number | null | undefined
): string | null {
  const total = typeof scoreTotal === 'number' && isFinite(scoreTotal) ? scoreTotal : null;
  const max = typeof scoreMax === 'number' && isFinite(scoreMax) ? scoreMax : null;

  if (total === null && max === null) {
    return null;
  }
  if (max === null) {
    return `${total}`;
  }
  if (total === null) {
    return `— / ${max}`;
  }
  return `${total} / ${max}`;
}

/** The document's own name, or a neutral fallback — never an empty heading on a timeline row. */
export function documentNameOf(request: PatientDocumentRequestDto): string {
  const name = request && request.PracticeDocument ? request.PracticeDocument.name : null;
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || 'Document';
}

/**
 * Turn the patient's document requests into timeline items.
 *
 * `patientId` is optional and, when given, is enforced. The service scopes its list to the open
 * patient already, so this is belt-and-braces — but the timeline is the one screen in the product
 * where a row from the wrong patient would be read as clinical fact about this one, and the check
 * costs a comparison.
 *
 * The instant used for BOTH `date` and `createdAt` is `returnedAt`. Using the request's own
 * `createdAt` would file a document under the day the practice ASKED for it, which can be weeks
 * before the patient sent it back — and the timeline is a record of what happened to the patient,
 * not of what the practice's admin did.
 */
export function toTimelineDocuments(
  requests: PatientDocumentRequestDto[] | null | undefined,
  patientId?: string | null
): TimelineDocumentItem[] {
  if (!Array.isArray(requests)) {
    return [];
  }

  const items: TimelineDocumentItem[] = [];

  for (const request of requests) {
    if (!request || !request.id) {
      continue;
    }
    if (patientId && request.patientId && String(request.patientId) !== String(patientId)) {
      continue;
    }
    // ALL THREE, not any. See the header — this is what excludes an outstanding ask, what makes
    // a rejected-and-re-asked row leave the timeline, and (§26) what keeps a document off the
    // clinical record until a clinician has put their name to it.
    if (request.status !== 'completed' || !request.returnedAt
      || request.reviewStatus !== 'validated') {
      continue;
    }

    const returnedAt = String(request.returnedAt);

    items.push({
      type: TIMELINE_DOCUMENT_TYPE,
      id: String(request.id),
      /*
       * REQUIRED BY THE EMAIL, and it was missing.
       *
       * Every other timeline row carries `patientId`, and `sendTimelineRecord` posts
       * `item.patientId` to `POST /patient-communications`. A returned-document row did not, so
       * the server received `undefined`, `Patient.findByPk(undefined)` returned null, and the
       * request died on `patient.groupId` with a 500 and no useful message.
       *
       * The DOWNLOAD never noticed: it reads the component's own `this.patient`. Only the email
       * crossed the wire, which is why this survived the cover-page work.
       */
      patientId: request.patientId ? String(request.patientId) : null,
      date: returnedAt,
      createdAt: returnedAt,
      documentName: documentNameOf(request),
      reviewStatus: request.reviewStatus ? String(request.reviewStatus) : null,
      reviewLabel: documentReviewLabel(request.reviewStatus),
      returnedFileCount: typeof request.returnedFileCount === 'number' ? request.returnedFileCount : 0,
      scoreTotal: typeof request.scoreTotal === 'number' ? request.scoreTotal : null,
      scoreMax: typeof request.scoreMax === 'number' ? request.scoreMax : null,
      scoreLabel: documentScoreLabel(request.scoreTotal, request.scoreMax),
      hasAiSummary: request.hasAiSummary === true,
      // §26. The name and date ride the LIST (they are not clinical prose); the observations do
      // not, and are filled in by the component's per-row detail read alongside the gallery.
      validatedByName: request.validatedByName ? String(request.validatedByName) : null,
      validatedAt: request.validatedAt ? String(request.validatedAt) : null,
      validationNote: null,
      // Filled in by the component from the per-row detail read. See the interface.
      galleryFiles: [],
      galleryExpiresAt: 0
    });
  }

  return items;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * THE RETURNED PAGES AS A GALLERY — the same idiom a clinical note uses for its media
 *
 * A note row draws `<app-file-gallery [fileObjects]="item.media">` and so, now, does a returned
 * document: the pages are the record, and a reviewer reading a timeline should see them rather
 * than a sentence counting them.
 *
 * `mapMediaToFileObject` IS THE MAPPER, imported rather than reimplemented, for the reason the
 * documents panel gives at its own copy of this: every gallery in the product is fed by that one
 * function, including on the rule that decides what a page even is — A PDF IS FILTERED OUT. Two
 * mappings would be two opinions about that, and the two screens would be found disagreeing by
 * somebody comparing a returned document with the same pages on the panel.
 *
 * ACTIVE FILES ONLY. A rejection deactivates what came back and re-opens the request (§13.2), and
 * `toTimelineDocuments` already drops a re-opened row from the timeline entirely — so superseded
 * pages can only reach here on a row that was rejected and then completed again, where they are
 * precisely the pages the practice refused. The panel shows them, struck through, because a
 * reviewer needs to see what a rejection note was written about. A clinical record does not.
 * ═════════════════════════════════════════════════════════════════════════════════════════ */

/** What one detail read yields: the pages, and the instant their links stop working. */
export interface TimelineDocumentGallery {
  files: FileObject[];
  /**
   * THE SAME PAGES IN THE SHAPE THE REST OF THE TIMELINE ALREADY READS — `{ mediaUrl, mediaType,
   * displayIndex }`, which is what a note's `item.media` holds.
   *
   * It exists so a returned document can use the timeline's OWN actions rather than growing a
   * parallel set: `downloadAsPdf`, `sendTimelineRecord` and the WhatsApp trigger all reach for
   * `item.media` and read `mediaUrl` off each row. A `FileObject` carries `url`, not `mediaUrl`,
   * so handing them the gallery array would embed nothing and report no error — the PDF would
   * simply come out with no pages in it.
   */
  media: TimelineDocumentMedia[];

  /* ── §A of `tasks/pdf-letterhead-placement.md` — the same pages, kept APART ──────────────
   *
   * `media` above is the two sets joined, which is what the WhatsApp, email and copy actions want:
   * one list of everything attached to this row. The COVER-PAGE export wants them separate — the
   * clinician's attachments belong on the cover beside their observations, and the patient's own
   * returned pages belong after it, one per page at full size.
   *
   * Added ALONGSIDE `media` rather than replacing it, so the three actions that already read it
   * are untouched and their specs stay green. That the existing gallery specs still pass is the
   * evidence this was additive.
   */

  /** The patient's returned pages. Full-page, chromeless, from page 2 of the export. */
  returnedMedia: TimelineDocumentMedia[];
  /** What the clinician attached when validating. Flows onto the cover, beside their text. */
  validationMedia: TimelineDocumentMedia[];

  /** Epoch ms. 0 when nothing has an expiry to report — see `documentGalleryIsStale`. */
  expiresAt: number;
}

/** One page, in the shape every timeline action reads. NOT a `FileObject` — see the gallery. */
export interface TimelineDocumentMedia {
  mediaUrl: string;
  mediaType: string;
  displayIndex: number;
}

/**
 * Map the detail DTO's active returned files into gallery tiles.
 *
 * A file with NO `url` is dropped here, and only here. The server returns such a row deliberately
 * — so the panel can tell a reviewer that a page exists and cannot be opened — but a gallery has
 * no way to draw "this page is unopenable", and a tile pointing at nothing is worse than an absent
 * one on a screen that offers no explanation beside it. The panel remains the place that accounts
 * for every page.
 */
export function documentGallery(
  detail: PatientDocumentRequestDto | null | undefined
): TimelineDocumentGallery {
  /*
   * §26 — ONE gallery, carrying the returned pages FIRST and the clinician's own attachments
   * after them.
   *
   * One list rather than two, because a timeline row reads like a clinical note: title, the
   * clinician's text, then the media. Two labelled galleries is the DOCUMENTS PANEL's answer, and
   * it is the right one there — a reviewer is deciding about provenance, so what the patient sent
   * has to be visibly separate from what the practice added. A clinician reading the record wants
   * the pages of the episode in order.
   *
   * `displayIndex` is re-numbered across the join. Both tables number from 0 independently, so
   * without this `mapMediaToFileObject`'s sort would interleave them — page 1, attachment 1,
   * page 2 — and the "returned first" order would be silently lost.
   */
  /*
   * EACH GROUP IS SORTED BY ITS OWN `displayIndex` BEFORE THEY ARE JOINED, and that order is then
   * frozen by the re-numbering below.
   *
   * Sorting after the join would not do: `displayIndex` is page order WITHIN a set, both tables
   * number from 0, and a single sort across the join interleaves them — returned page 1, attached
   * image 1, returned page 2. Re-numbering without sorting first is just as wrong the other way:
   * it would freeze whatever order the server happened to return the pages in, which is how a
   * ten-page scan ends up unreadable.
   */
  const byPage = (a: PatientDocumentReturnedFileDto, b: PatientDocumentReturnedFileDto) =>
    (typeof a.displayIndex === 'number' ? a.displayIndex : 0)
    - (typeof b.displayIndex === 'number' ? b.displayIndex : 0);

  const returned = activeReturnedFiles(detail).filter((file) => !!file.url).sort(byPage);
  const validation = activeValidationFiles(detail).filter((file) => !!file.url).sort(byPage);
  const files = [...returned, ...validation];
  if (!detail || !files.length) {
    return { files: [], media: [], returnedMedia: [], validationMedia: [], expiresAt: 0 };
  }

  const mapped = mapMediaToFileObject(files.map((file, index) => ({
    id: file.id,
    mediaUrl: file.url,
    mediaType: file.mediaType,
    mediaFileName: file.mediaFileName,
    displayIndex: index,
    patientId: detail.patientId
  }))) || [];

  // The EARLIEST expiry, not the latest: one dead page in a gallery of ten is still a clinical
  // record with a hole in it, so the whole row is re-read as soon as any of it is about to die.
  let expiresAt = 0;
  for (const file of files) {
    const at = file.expiresAt ? Date.parse(String(file.expiresAt)) : NaN;
    if (!isFinite(at)) continue;
    if (expiresAt === 0 || at < expiresAt) expiresAt = at;
  }

  /*
   * Each set is numbered from 0 IN ITS OWN LIST, and the joined `media` is renumbered across both.
   * They are separate numbering spaces on purpose: `returnedMedia` is "page 1, 2, 3 of what the
   * patient sent", which is the order the cover-page export prints them in, and it must not
   * inherit an offset from however many attachments happen to precede it in the joined list.
   */
  const toMedia = (rows: PatientDocumentReturnedFileDto[]) => rows.map((file, index) => ({
    mediaUrl: String(file.url),
    mediaType: String(file.mediaType || 'image/jpeg'),
    displayIndex: index,
  }));

  const media = files.map((file, index) => ({
    mediaUrl: String(file.url),
    mediaType: String(file.mediaType || 'image/jpeg'),
    displayIndex: index,
  }));

  return {
    files: mapped,
    media,
    returnedMedia: toMedia(returned),
    validationMedia: toMedia(validation),
    expiresAt,
  };
}

/**
 * Whether a row's pages need re-reading — `now` is passed in so this is testable without clocks.
 *
 * `graceMs` is subtracted so the re-read is fired BEFORE the links die rather than after a
 * clinician has already looked at a broken page. A gallery that has never been read
 * (`expiresAt: 0`) is not stale; it is absent, which is a different question and the caller's.
 */
export function documentGalleryIsStale(
  expiresAt: number, now: number, graceMs: number
): boolean {
  if (!expiresAt) return false;
  return now >= (expiresAt - graceMs);
}

/**
 * THE ROW AS TEXT — what the download, the email and the WhatsApp message all carry.
 *
 * The timeline's existing actions take a `content` string and a media array; this is the string
 * for a returned document, in the same shape `compileAdmissionRecord` produces for an admission.
 * Written here, in the pure module, rather than in the component: it is the wording a patient's
 * document goes out to a third party under, and it is worth being able to assert exactly.
 *
 * The OBSERVATIONS ARE INCLUDED and that is the point of the whole row — a PDF of the pages with
 * no clinician's reading attached is just the scan the patient sent. What is deliberately NOT
 * here is the review note: that is a rejection reason written for the practice's own staff, about
 * an attempt that was refused, and it has no place in a document sent onward.
 */
export function compileDocumentReturn(item: {
  documentName?: string;
  date?: string;
  validatedByName?: string | null;
  validatedAt?: string | null;
  validationNote?: string | null;
  returnedFileCount?: number;
} | null, options: { includeValidationNote?: boolean } = {}): string {
  if (!item) return '';

  const lines: string[] = [];
  lines.push(item.documentName || 'Returned document');
  lines.push('');

  if (item.returnedFileCount) {
    lines.push(`${item.returnedFileCount} file${item.returnedFileCount === 1 ? '' : 's'} returned `
      + 'by the patient.');
  }
  if (item.validatedByName) {
    lines.push(`Validated by ${item.validatedByName}.`);
  }
  /*
   * THE OBSERVATION IS OPTIONAL IN THE OUTPUT, and only one caller turns it off.
   *
   * The timeline's export menu can drop the cover page, which is where the validating clinician's
   * observation lives in the PDF. The email built alongside that PDF has to drop it too - a note
   * removed from the attachment and left in the message body has not been removed from anything.
   *
   * DEFAULT ON. Every other caller (the clipboard, the on-screen row) is the practice reading its
   * own record, and that is exactly what the note is for.
   */
  if (item.validationNote && options.includeValidationNote !== false) {
    lines.push('');
    lines.push(item.validationNote);
  }

  return lines.join('\n');
}

/**
 * The one line of searchable text for a document row.
 *
 * The AI SUMMARY IS NOT IN IT, and that is not an oversight. `aiSummary` is encrypted clinical
 * prose that the list endpoint deliberately does not serve (`toStaffDto` deletes it unless the
 * single-row read asked for it), so it is not in the DTO this file receives and putting it into a
 * client-side search index would mean shipping every patient's summaries to the browser to make
 * them findable. The name, the review state and the score are what a person searches a timeline
 * for.
 */
export function documentSearchText(item: TimelineDocumentItem): string {
  return [
    'Returned Document',
    item.documentName,
    item.reviewLabel,
    item.scoreLabel ? `score ${item.scoreLabel}` : ''
  ].filter(Boolean).join(' ');
}
